import { useCallback, useRef, useState } from 'react'

import { InlineVideoPlayer } from 'components/agents/InlineVideoPlayer'
import { VideoSegmentStrip } from 'components/agents/VideoSegmentStrip'
import styles from 'components/chat-components/AgentMessage.module.css'
import { getNextPlaybackSpeed } from 'components/chat-components/videoPlaybackSpeed'
import { Button } from 'components/ui/Button'

import type { VideoClip } from '@startech/openapi'
import type { InlineVideoPlayerController } from 'components/agents/InlineVideoPlayer'
import type {
  ContentRendererProperties,
  UploadedVideoSource,
  VideoSideBySideContent,
} from 'components/chat-components/AgentMessage.types'
import type { ReactElement } from 'react'

type VideoSideBySideRendererProperties = {
  content: VideoSideBySideContent
} & Pick<ContentRendererProperties, 'mux' | 'videoDuration' | 'videoSources' | 'videoUrl'>

type ClipController = InlineVideoPlayerController
type ResolvedClip = {
  clip: VideoClip
  label: string
  playbackRate: number
  source?: UploadedVideoSource
}

const VIDEO_SIDE_BY_SIDE_CLIP_COUNT = 2

const isContentPending = (content: VideoSideBySideContent): boolean => {
  return 'pending' in content && content.pending === true
}

const getClipRange = (clip: VideoClip): string => {
  return `${clip.startTime}-${clip.endTime}`
}

const resolveClipSource = (
  clip: VideoClip,
  videoSources: UploadedVideoSource[] | undefined
): UploadedVideoSource | undefined => {
  return videoSources?.find(source => source.fileId === clip.fileId)
}

const buildResolvedClips = (
  content: VideoSideBySideContent,
  videoSources: UploadedVideoSource[] | undefined
): ResolvedClip[] => {
  const clips = content.clips.slice(0, VIDEO_SIDE_BY_SIDE_CLIP_COUNT)

  return clips.map((clip, index) => {
    const source = resolveClipSource(clip, videoSources)
    return {
      clip,
      label: clip.label ?? source?.label ?? `Clip ${index + 1}`,
      playbackRate: clip.playbackRate ?? content.playbackRate ?? 1,
      source,
    }
  })
}

const getEffectiveSources = (
  videoSources: UploadedVideoSource[] | undefined,
  fallbackSource: UploadedVideoSource | undefined
): UploadedVideoSource[] => {
  if (videoSources?.length) {
    return videoSources
  }

  return fallbackSource ? [fallbackSource] : []
}

const getResolvedClipRate = (
  clipRates: Partial<Record<number, number>>,
  resolvedClips: ResolvedClip[],
  index: number
): number => {
  const userRate = clipRates[index]
  if (typeof userRate === 'number') {
    return userRate
  }

  return resolvedClips[index]?.playbackRate ?? 1
}

export const VideoSideBySideRenderer = ({
  content,
  mux,
  videoDuration,
  videoSources,
  videoUrl,
}: VideoSideBySideRendererProperties): ReactElement => {
  /* --- Section 1: Hooks --- */
  const controllersReference = useRef(new Map<number, ClipController>())
  const isSyncingReference = useRef(false)

  /* --- Section 2: Local State --- */
  const [playbackTimes, setPlaybackTimes] = useState<Partial<Record<number, number>>>({})
  const [clipRates, setClipRates] = useState<Partial<Record<number, number>>>({})

  /* --- Section 3: Derived Values --- */
  const isPending = isContentPending(content)
  const fallbackSource: UploadedVideoSource | undefined = videoUrl
    ? {
        fileId: 'primary',
        isPrimary: true,
        label: 'Original video',
        mux,
        videoDuration,
        videoUrl,
      }
    : undefined
  const effectiveSources = getEffectiveSources(videoSources, fallbackSource)
  const resolvedClips = buildResolvedClips(content, effectiveSources)
  const isLoop = content.loop ?? true

  /* --- Section 4: Event Handlers --- */
  const handleControllerReady = useCallback(
    (index: number, controller: ClipController) => {
      controllersReference.current.set(index, controller)
      controller.setPlaybackRate(getResolvedClipRate(clipRates, resolvedClips, index))
    },
    [clipRates, resolvedClips]
  )

  const handleTimeUpdate = useCallback((index: number, currentTime: number) => {
    setPlaybackTimes(previous => ({ ...previous, [index]: currentTime }))
  }, [])

  const handlePlayStateChange = useCallback((index: number, isPlaying: boolean) => {
    if (isSyncingReference.current) {
      return
    }

    isSyncingReference.current = true
    for (const [controllerIndex, controller] of Array.from(controllersReference.current.entries())) {
      if (controllerIndex === index) {
        continue
      }
      if (isPlaying) {
        controller.play()
      } else {
        controller.pause()
      }
    }
    queueMicrotask(() => {
      isSyncingReference.current = false
    })
  }, [])

  const handleCycleSpeed = useCallback(
    (index: number) => {
      setClipRates(previous => {
        const currentRate = getResolvedClipRate(previous, resolvedClips, index)
        const nextRate = getNextPlaybackSpeed(currentRate)
        controllersReference.current.get(index)?.setPlaybackRate(nextRate)
        return {
          ...previous,
          [index]: nextRate,
        }
      })
    },
    [resolvedClips]
  )

  /* --- Section 6: Render --- */
  return (
    <div className={styles.contentVideoSideBySide}>
      <div className={styles.videoSideBySideGrid} role="group" aria-label="Video clips for comparison">
        {resolvedClips.map((resolvedClip, index) => {
          const { clip, source } = resolvedClip
          const playbackRate = getResolvedClipRate(clipRates, resolvedClips, index)
          const uploadedSource = source
          const readyUploadedSource = !isPending && uploadedSource?.videoUrl ? uploadedSource : undefined
          const isReady = readyUploadedSource !== undefined
          const clipKey = `${clip.fileId}:${getClipRange(clip)}`

          return (
            <div className={styles.videoSideBySideCell} key={clipKey}>
              <div className={styles.videoSideBySideFrame}>
                <div className={styles.videoSideBySideChrome}>
                  <span className={styles.videoSideBySideClipBadge}>{index + 1}</span>
                  <Button
                    className={styles.videoSideBySideSpeedButton}
                    onClick={() => handleCycleSpeed(index)}
                    type="button"
                    variant="plain"
                  >
                    {playbackRate}x
                  </Button>
                </div>
                {readyUploadedSource ? (
                  <InlineVideoPlayer
                    videoUrl={readyUploadedSource.videoUrl}
                    mux={readyUploadedSource.mux}
                    startTime={clip.startTime}
                    endTime={clip.endTime}
                    isLoop={isLoop}
                    playbackRate={playbackRate}
                    onControllerReady={controller => handleControllerReady(index, controller)}
                    onPlayStateChange={isPlaying => handlePlayStateChange(index, isPlaying)}
                    onTimeUpdate={currentTime => handleTimeUpdate(index, currentTime)}
                    className={styles.videoSegmentPlayer}
                  />
                ) : undefined}
                {isReady ? undefined : (
                  <div
                    className={styles.videoSegmentSkeleton}
                    aria-busy="true"
                    aria-label={`Loading clip ${index + 1}`}
                  >
                    <span className={styles.videoSegmentSpinner} />
                  </div>
                )}
                {readyUploadedSource ? (
                  <VideoSegmentStrip
                    mux={readyUploadedSource.mux}
                    startTime={clip.startTime}
                    endTime={clip.endTime}
                    videoDuration={readyUploadedSource.videoDuration}
                    currentPlaybackTime={playbackTimes[index]}
                  />
                ) : undefined}
              </div>
              <div className={styles.videoSideBySideMeta}>
                <div className={styles.videoSideBySideTitle}>{resolvedClip.label}</div>
                <div className={styles.videoSegmentRange}>{getClipRange(clip)}</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
